/**
 * Notification Center Component
 * 
 * Displays in-app notifications and notification management
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { 
  Bell, 
  X, 
  Check, 
  AlertCircle, 
  Info, 
  CheckCircle, 
  AlertTriangle,
  ExternalLink,
  Trash2
} from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';
import { AppNotification } from '../../@types/notification';
import { ThemeColors, Spacing, Typography, Shadows } from '../../styles/ThemeStyles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const NotificationCenterContainer = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 400px;
  height: 100vh;
  background: white;
  box-shadow: ${Shadows.lg};
  transform: translateX(${props => props.isOpen ? '0' : '100%'});
  transition: transform 0.3s ease;
  z-index: 1000;
  display: flex;
  flex-direction: column;

  @media (max-width: 480px) {
    width: 90%;
  }
`;

const Header = styled.div`
  padding: ${Spacing.md} ${Spacing.lg};
  border-bottom: 1px solid ${ThemeColors.border.light};
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${ThemeColors.primary.main};
  color: white;
  gap: ${Spacing.sm};
  flex-wrap: wrap;

  @media (max-width: 480px) {
    padding: ${Spacing.md};
  }
`;

const HeaderTitle = styled.h2`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.bold};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  flex-shrink: 0;

  @media (max-width: 480px) {
    font-size: ${Typography.fontSize.md};
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: ${Spacing.sm};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;


const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const NotificationList = styled.div`
  flex: 1;
`;

const NotificationItem = styled.div<{ priority: string; isRead: boolean; priorityColor?: string }>`
  padding: ${Spacing.md} ${Spacing.lg};
  border-bottom: 1px solid ${ThemeColors.border.light};
  cursor: pointer;
  transition: background-color 0.2s ease;
  opacity: ${props => props.isRead ? 0.6 : 1};
  border-left: 4px solid ${props => props.priorityColor || ThemeColors.border.light};
  
  &:hover {
    background: ${ThemeColors.background.light};
  }

  @media (max-width: 480px) {
    padding: ${Spacing.md};
  }
`;

const NotificationHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${Spacing.sm};
  margin-bottom: ${Spacing.sm};
`;

const NotificationIcon = styled.div<{ color?: string }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${props => props.color || ThemeColors.primary.main};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
`;

const NotificationContent = styled.div`
  flex: 1;
`;

const NotificationTitle = styled.h3`
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: 0 0 ${Spacing.xs} 0;
`;

const NotificationMessage = styled.p`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};
  margin: 0 0 ${Spacing.sm} 0;
  line-height: 1.4;
`;

const NotificationTime = styled.span`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.hint};
`;

const NotificationActions = styled.div`
  display: flex;
  gap: ${Spacing.sm};
  margin-top: ${Spacing.sm};
`;

const ActionButton = styled.button<{ variant?: string }>`
  padding: ${Spacing.xs} ${Spacing.sm};
  border: 1px solid ${ThemeColors.border.main};
  border-radius: 4px;
  background: white;
  color: ${ThemeColors.text.primary};
  font-size: ${Typography.fontSize.xs};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background: ${ThemeColors.background.secondary};
  }
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: ${ThemeColors.primary.main};
          color: white;
          border-color: ${ThemeColors.primary.main};
          
          &:hover {
            background: ${ThemeColors.primary.dark};
          }
        `;
      case 'danger':
        return `
          background: ${ThemeColors.error.main};
          color: white;
          border-color: ${ThemeColors.error.main};
          
          &:hover {
            background: ${ThemeColors.error.dark};
          }
        `;
      default:
        return '';
    }
  }}
`;

const EmptyState = styled.div`
  padding: ${Spacing.xxl};
  text-align: center;
  color: ${ThemeColors.text.secondary};
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${ThemeColors.background.light};
  color: ${ThemeColors.text.hint};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${Spacing.lg} auto;
`;

const EmptyTitle = styled.h3`
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.primary};
  margin: 0 0 ${Spacing.sm} 0;
`;

const EmptyMessage = styled.p`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  margin: 0;
`;

const MarkAllReadButton = styled.button`
  padding: ${Spacing.xs} ${Spacing.md};
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  @media (max-width: 480px) {
    padding: ${Spacing.xs} ${Spacing.sm};
    font-size: 11px;
  }
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  color: ${ThemeColors.text.secondary};
  cursor: pointer;
  padding: ${Spacing.xs};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  opacity: 0.6;
  
  &:hover {
    background: ${ThemeColors.error.light};
    color: ${ThemeColors.error.main};
    opacity: 1;
  }
`;

interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenSettings?: () => void;
}

export const NotificationCenter = React.memo<NotificationCenterProps>(({
  isOpen,
  onClose
}) => {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead, deleteNotification } = useNotifications();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(loading);
  }, [loading]);

  const handleNotificationClick = useCallback(async (notification: AppNotification) => {
    try {
      if (!notification.read) {
        await markAsRead(notification.id);
      }

      // Navigate based on notification metadata
      let route: string | null = null;

      if (notification.metadata?.link) {
        // Use the link if provided, but check if it needs role prefix
        route = notification.metadata.link;
        
        // If link starts with /store-details but user is manager, change to /manager/store-details
        if (route.startsWith('/store-details/') && userRole === 'manager') {
          route = route.replace('/store-details/', '/manager/store-details/');
        }
        
        // If link starts with /manager/resources but user is sales rep, change to /resources
        if (route.startsWith('/manager/resources/') && userRole === 'sales-representative') {
          route = route.replace('/manager/resources/', '/resources/');
        }
      } else if (notification.metadata?.storeId) {
        // Construct route based on user role
        const storeId = notification.metadata.storeId;
        route = userRole === 'manager' 
          ? `/manager/store-details/${storeId}`
          : `/store-details/${storeId}`;
      } else if (notification.metadata?.orderId && notification.metadata?.storeId) {
        // For order notifications
        const storeId = notification.metadata.storeId;
        const orderId = notification.metadata.orderId;
        route = userRole === 'manager'
          ? `/manager/order-details/${storeId}/${orderId}`
          : `/store-details/${storeId}`;
      } else if (notification.metadata?.resourceId) {
        // For resource notifications
        const resourceId = notification.metadata.resourceId;
        route = userRole === 'manager' 
          ? `/manager/resources/${resourceId}`
          : `/resources/${resourceId}`;
      }

      if (route) {
        console.log('Navigating to:', route, 'for notification:', notification.type);
        navigate(route);
        onClose();
      } else {
        console.warn('No route found for notification:', notification);
      }
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  }, [markAsRead, navigate, onClose, userRole]);

  const handleActionClick = useCallback((action: string, notificationId: string) => {
    console.log('Action clicked:', action, 'for notification:', notificationId);
    // Handle action based on type
  }, []);

  const handleDeleteNotification = useCallback(async (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this notification?')) {
      try {
        await deleteNotification(notificationId);
      } catch (error) {
        console.error('Error deleting notification:', error);
      }
    }
  }, [deleteNotification]);

  const handleMarkAllRead = useCallback(async () => {
    await markAllAsRead();
  }, [markAllAsRead]);

  const getIcon = useCallback((iconName?: string) => {
    switch (iconName) {
      case 'CheckCircle':
        return <CheckCircle size={16} />;
      case 'Info':
        return <Info size={16} />;
      case 'AlertCircle':
        return <AlertCircle size={16} />;
      case 'AlertTriangle':
        return <AlertTriangle size={16} />;
      default:
        return <Info size={16} />;
    }
  }, []);

  const formatTime = useCallback((timestamp: any) => {
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff / (1000 * 60));

    if (hours > 24) {
      return date.toLocaleDateString();
    } else if (hours > 0) {
      return `${hours}h ago`;
    } else if (minutes > 0) {
      return `${minutes}m ago`;
    } else {
      return 'Just now';
    }
  }, []);

  const getNoteNotificationTitle = useCallback((notification: AppNotification) => {
    if (notification.type !== 'NOTE_ADDED') {
      return notification.title || 'Notification';
    }
    
    // Check if metadata exists and has the new fields
    if (!notification.metadata) {
      return notification.title || 'New Note Added';
    }
    
    const customerNo = notification.metadata.customerNo;
    const storeName = notification.metadata.storeName;
    
    if (customerNo && storeName) {
      return `New Note - ${customerNo} - ${storeName}`;
    } else if (customerNo) {
      return `New Note - ${customerNo}`;
    } else if (storeName) {
      return `New Note - ${storeName}`;
    }
    
    return notification.title || 'New Note Added';
  }, []);

  const getNoteNotificationMessage = useCallback((notification: AppNotification) => {
    if (notification.type !== 'NOTE_ADDED') {
      return notification.message || '';
    }
    
    // Check if metadata exists
    if (!notification.metadata) {
      return notification.message || '';
    }
    
    const parts: string[] = [];
    
    // Add creator name
    const creatorFullName = notification.metadata.creatorFullName;
    const creatorFirstName = notification.metadata.creatorFirstName;
    const creatorLastName = notification.metadata.creatorLastName;
    
    if (creatorFullName) {
      parts.push(`By ${creatorFullName}`);
    } else if (creatorFirstName && creatorLastName) {
      parts.push(`By ${creatorFirstName} ${creatorLastName}`);
    }
    
    // Add first 6 words of note text
    const noteText = notification.metadata.noteText;
    if (noteText && noteText.trim()) {
      const words = noteText.trim().split(/\s+/).slice(0, 6);
      if (words.length > 0) {
        const preview = words.join(' ');
        parts.push(preview + (noteText.split(/\s+/).length > 6 ? '...' : ''));
      }
    }
    
    // If we have parts, join them; otherwise fall back to original message
    if (parts.length > 0) {
      return parts.join(' • ');
    }
    
    // Fallback to original message if no enhanced data available
    return notification.message || '';
  }, []);

  const getPriorityColor = useCallback((priority: string) => {
    switch (priority) {
      case 'CRITICAL':
        return ThemeColors.error.main;
      case 'HIGH':
        return ThemeColors.warning.main;
      case 'MEDIUM':
        return ThemeColors.primary.main;
      case 'LOW':
        return ThemeColors.border.light;
      default:
        return ThemeColors.primary.main;
    }
  }, []);

  const getPriorityIcon = useCallback((type: string) => {
    switch (type) {
      case 'NOTE_ADDED':
        return 'Info';
      case 'ORDER_SUBMITTED':
        return 'AlertCircle';
      case 'RESOURCE_ADDED':
        return 'CheckCircle';
      case 'ANNOUNCEMENT_CREATED':
        return 'AlertTriangle';
      default:
        return 'Info';
    }
  }, []);

  return (
    <NotificationCenterContainer isOpen={isOpen}>
      <Header>
        <HeaderTitle>
          <Bell size={20} />
          Notifications {unreadCount > 0 && `(${unreadCount})`}
        </HeaderTitle>
        <HeaderActions>
          {unreadCount > 0 && (
            <MarkAllReadButton onClick={handleMarkAllRead}>
              Mark All Read
            </MarkAllReadButton>
          )}
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </HeaderActions>
      </Header>

      <Content>
        {isLoading ? (
          <EmptyState>
            <EmptyIcon>
              <Bell size={32} />
            </EmptyIcon>
            <EmptyTitle>Loading notifications...</EmptyTitle>
          </EmptyState>
        ) : notifications.length === 0 ? (
          <EmptyState>
            <EmptyIcon>
              <Bell size={32} />
            </EmptyIcon>
            <EmptyTitle>No notifications</EmptyTitle>
            <EmptyMessage>You're all caught up!</EmptyMessage>
          </EmptyState>
        ) : (
          <NotificationList>
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                priority={notification.priority}
                isRead={notification.read}
                priorityColor={getPriorityColor(notification.priority)}
                onClick={() => handleNotificationClick(notification)}
              >
                <NotificationHeader>
                  <NotificationIcon color={getPriorityColor(notification.priority)}>
                    {getIcon(getPriorityIcon(notification.type))}
                  </NotificationIcon>
                  <NotificationContent>
                    <NotificationTitle>{getNoteNotificationTitle(notification)}</NotificationTitle>
                    <NotificationMessage>{getNoteNotificationMessage(notification) || notification.message}</NotificationMessage>
                    <NotificationTime>{formatTime(notification.createdAt)}</NotificationTime>
                  </NotificationContent>
                  <DeleteButton
                    onClick={(e) => handleDeleteNotification(e, notification.id)}
                    title="Delete notification"
                  >
                    <Trash2 size={16} />
                  </DeleteButton>
                </NotificationHeader>
                
                {notification.metadata?.link || notification.metadata?.storeId || notification.metadata?.resourceId ? (
                  <NotificationActions>
                    <ActionButton
                      variant="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNotificationClick(notification);
                      }}
                    >
                      View Details
                    </ActionButton>
                  </NotificationActions>
                ) : null}
              </NotificationItem>
            ))}
          </NotificationList>
        )}
      </Content>
    </NotificationCenterContainer>
  );
});
